/**
 * Page displayed after a Stripe checkout redirect, showing the payment
 * result.
 *
 * Author: Victor Chevillotte <victor.chevillotte@ircamamplify.com>
 * Copyright © Ircam Amplify 2025-2026
 */
import { fetchCompanyConsumption, fetchUser } from '@/api'
import Button from '@/components/Button/Button'
import FullScreenLayout from '@/components/FullScreenLayout'
import { baseURL } from '@/config/env'
import { logger } from '@/config/logger'
import { apiRequest } from '@amplify3/duplo-back-end'
import { useCallback, useEffect, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import { useNavigate } from 'react-router-dom'

/**
 * Page displayed after a Stripe checkout redirect.
 * Polls the backend until the payment session is confirmed, then
 * shows a confirmation message and a button to return to the app.
 */
export const CheckoutResultPage = () => {
  const navigate = useNavigate()
  /** Stripe payment session status, e.g. 'complete' or 'open'. */
  const [stripeStatus, setStripeStatus] = useState<string | null>(null)
  /** Email address of the customer, shown in the confirmation message. */
  const [customerEmail, setCustomerEmail] = useState('')

  const isComplete = stripeStatus === 'complete'

  /**
   * Fetch the Stripe session status from the backend using the
   * session_id URL parameter set by Stripe after the redirect.
   * Updates stripeStatus and customerEmail on success.
   */
  const fetchSession = useCallback(async () => {
    const sessionId = new URLSearchParams(window.location.search).get(
      'session_id'
    )

    try {
      const data = await apiRequest({
        baseURL,
        url: `/stripe/session-status?sessionId=${sessionId}`,
      })

      setStripeStatus(data.stripe_status)
      setCustomerEmail(data.customer_email)
    } catch (error) {
      logger.error('Error fetching session status:', error)
    }
  }, [])

  // Poll until Stripe status is complete.
  useEffect(() => {
    if (isComplete) return

    const intervalId = setInterval(async () => {
      try {
        await fetchSession()
      } catch (error) {
        logger.error('Error polling subscription status:', error)
      }
    }, 3000)

    // useEffect's cleanup: stop the interval on unmount or before
    // the effect re-runs (e.g. when stripeStatus changes to 'complete').
    return () => clearInterval(intervalId)
  }, [isComplete, fetchSession])

  /**
   * Refresh user data in the background and navigate to the main page.
   */
  const handleNavigateBack = () => {
    fetchUser(true)
    fetchCompanyConsumption(true)
    navigate('/')
  }

  return isComplete ? (
    <FullScreenLayout title="" subtitle="">
      <div className="text-center mt-8 mb-8">
        <h3 style={{ fontWeight: 'bold', marginBottom: '2rem' }}>
          Purchase confirmation
        </h3>
        <p className="mb-4">
          You will receive an email at <strong>{customerEmail}</strong>{' '}
          confirming your payment.
        </p>
        <p className="mb-8">You can start right away!</p>
      </div>
      <div className="w-full mt-8">
        <div className="w-60 mx-auto">
          <Button
            label="Process my files"
            className="w-full cursor-pointer"
            onClick={handleNavigateBack}
          />
        </div>
      </div>
    </FullScreenLayout>
  ) : (
    <FullScreenLayout
      title={<Skeleton width={200} />}
      subtitle={<Skeleton width={300} />}
    >
      <div className="w-60 mx-auto">
        <Skeleton />
      </div>
    </FullScreenLayout>
  )
}
